import { ReactElement, useEffect, useMemo, useState } from "react";

import { Button } from "../catalyst/Button";
import { Checkbox } from "../catalyst/Checkbox";
import { Input } from "../catalyst/Input";
import { Label } from "../catalyst/Label";
import { Select } from "../catalyst/Select";

type DateFormatPreset = {
  example: string;
  label: string;
  value: string; // liquid format string like "%Y-%m-%d"
};

const DATE_FORMAT_PRESETS: DateFormatPreset[] = [
  {
    example: "2025-12-19T14:03:12Z",
    label: "ISO 8601 UTC",
    value: "%Y-%m-%dT%H:%M:%SZ",
  },
  {
    example: "2025-12-19T14:03Z",
    label: "ISO 8601 (no seconds)",
    value: "%Y-%m-%dT%H:%MZ",
  },
  { example: "2025-12-19", label: "YYYY-MM-DD", value: "%Y-%m-%d" },
  { example: "2025/12/19", label: "YYYY/MM/DD", value: "%Y/%m/%d" },
  { example: "19/12/2025", label: "DD/MM/YYYY", value: "%d/%m/%Y" },
  { example: "1766152992", label: "Unix epoch seconds", value: "%s" },
  {
    example: "1766152992000",
    label: "Unix epoch milliseconds",
    value: "%s%3N",
  }, // depends on engine support
];

type OffsetUnit = "days" | "hours" | "minutes" | "weeks";

const UNIT_TO_SECONDS: Record<OffsetUnit, number> = {
  days: 60 * 60 * 24,
  hours: 60 * 60,
  minutes: 60,
  weeks: 60 * 60 * 24 * 7,
};

export type DateSnippetPopupProps = {
  left: number;
  onClose: () => void;
  onInsert: (snippet: string) => void;
  top: number;
};

const DateSnippetPopup = ({
  left,
  onClose,
  onInsert,
  top,
}: DateSnippetPopupProps): ReactElement => {
  const [offsetEnabled, setOffsetEnabled] = useState(true);
  const [direction, setDirection] = useState<"minus" | "plus">("minus");
  const [amount, setAmount] = useState<number>(3);
  const [unit, setUnit] = useState<OffsetUnit>("days");

  const [formatPreset, setFormatPreset] = useState<string>(
    DATE_FORMAT_PRESETS[0].value,
  );

  const format = formatPreset;

  const offsetSeconds = useMemo(() => {
    if (!offsetEnabled) return 0;
    const safeAmount = Number.isFinite(amount)
      ? Math.max(0, Math.floor(amount))
      : 0;
    return safeAmount * UNIT_TO_SECONDS[unit];
  }, [amount, unit, offsetEnabled]);

  const snippet = useMemo(() => {
    // If no offset, keep it simple.
    if (!offsetEnabled || offsetSeconds === 0) {
      return `{{ 'now' | date: '${format}' }}`;
    }

    // For offset: convert "now" -> epoch seconds -> +/- seconds -> format
    return `{{ 'now' | date: '%s' | ${direction}: ${offsetSeconds} | date: '${format}' }}`;
  }, [offsetEnabled, offsetSeconds, direction, format]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
      if (e.key === "Enter") onInsert(snippet);
    };
    window.addEventListener("keydown", onKeyDown);
    return (): void => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, onInsert, snippet]);

  return (
    <div
      className="fixed z-[10000] w-[360px] rounded-[10px] border border-white/10 bg-transparent p-3 text-xs text-white shadow-[0_20px_40px_rgba(0,0,0,0.35)] dark:bg-zinc-900/75"
      onMouseDown={(e) => {
        // Only prevent default if clicking on the container itself, not its children
        if (e.target === e.currentTarget) {
          e.preventDefault();
        }
      }}
      style={{ left, top }}
    >
      <div className="flex items-center justify-between">
        <div className="text-[13px] font-bold">Insert date snippet</div>
        <Button
          aria-label="Close"
          className="h-4 w-4 border-0 bg-transparent p-0 text-gray-400 hover:text-gray-300"
          onClick={onClose}
          plain
          size="xs"
        >
          ×
        </Button>
      </div>

      {/* Base */}
      <div className="mt-2.5 flex items-center gap-2">
        <Label className="text-xs text-gray-400">Base</Label>
        <div className="rounded-lg border border-gray-800 bg-[#0B1220] px-2 py-1.5">
          <span className="opacity-80">Use:</span>{" "}
          <code className="font-mono">"now"</code>
        </div>
      </div>

      {/* Offset */}
      <div className="mt-3">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-gray-400">Offset</Label>
          <label className="flex cursor-pointer items-center gap-2">
            <Checkbox checked={offsetEnabled} onChange={setOffsetEnabled} />
            <span className="text-xs">Enable</span>
          </label>
        </div>

        <div className="mt-2 grid grid-cols-[90px_1fr_1fr] gap-2">
          <Select
            className="text-xs [&>select]:py-1 [&>select]:text-xs"
            data-disabled={!offsetEnabled ? "" : undefined}
            disabled={!offsetEnabled}
            onChange={(e) => setDirection(e.target.value as "minus" | "plus")}
            value={direction}
          >
            <option value="minus">- subtract</option>
            <option value="plus">+ add</option>
          </Select>

          <Input
            className="text-xs [&>input]:py-1 [&>input]:text-xs"
            data-disabled={!offsetEnabled ? "" : undefined}
            disabled={!offsetEnabled}
            min={0}
            onChange={(e) => setAmount(parseInt(e.target.value || "0", 10))}
            step={1}
            type="number"
            value={amount}
          />

          <Select
            className="text-xs [&>select]:py-1 [&>select]:text-xs"
            data-disabled={!offsetEnabled ? "" : undefined}
            disabled={!offsetEnabled}
            onChange={(e) => setUnit(e.target.value as OffsetUnit)}
            value={unit}
          >
            <option value="minutes">minutes</option>
            <option value="hours">hours</option>
            <option value="days">days</option>
            <option value="weeks">weeks</option>
          </Select>
        </div>
      </div>

      {/* Format */}
      <div className="mt-3">
        <Label className="mb-1.5 text-xs text-gray-400">Format</Label>

        <div className="grid grid-cols-1 gap-2">
          <Select
            className="text-xs [&>select]:py-1 [&>select]:text-xs"
            onChange={(e) => setFormatPreset(e.target.value)}
            value={formatPreset}
          >
            {DATE_FORMAT_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label} — {p.example}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-3 flex justify-end gap-2">
        <Button onClick={onClose} outline size="xs">
          Cancel
        </Button>
        <Button onClick={() => onInsert(snippet)} size="xs">
          Insert
        </Button>
      </div>
    </div>
  );
};

export default DateSnippetPopup;
